import type { AuthResult, DefaultResponseBody, OtpSendResult } from "@gerama/types";
import type { ApiClient } from "../client";

export interface SessionContext {
  deviceId?: string;
  deviceName?: string;
  platform?: string;
  appVersion?: string;
}

/** Same flows `delkok-app`'s Flutter client uses: phone/OTP + Google ID token, both settling in one `AuthResult` shape. */
export function createCoreAuthApi(client: ApiClient) {
  return {
    sendOtp: (phone: string) =>
      client
        .request<DefaultResponseBody<OtpSendResult>>("/auth/otp/send", {
          method: "POST",
          body: { phone },
        })
        .then((res) => res.data as OtpSendResult),

    verifyOtp: (phone: string, code: string, session: SessionContext = {}) =>
      client
        .request<DefaultResponseBody<AuthResult>>("/auth/otp/verify", {
          method: "POST",
          body: { phone, code, ...session },
        })
        .then((res) => res.data as AuthResult),

    /** Phone + password sign-in. */
    login: (phone: string, password: string, session: SessionContext = {}) =>
      client
        .request<DefaultResponseBody<AuthResult>>("/auth/login", {
          method: "POST",
          body: { phone, password, ...session },
        })
        .then((res) => res.data as AuthResult),

    /** New account: the OTP sent to `phone` proves the number, `password` becomes the sign-in password. */
    register: (
      body: { phone: string; password: string; code: string; name?: string; email?: string },
      session: SessionContext = {},
    ) =>
      client
        .request<DefaultResponseBody<AuthResult>>("/auth/register", {
          method: "POST",
          body: { ...body, ...session },
        })
        .then((res) => res.data as AuthResult),

    /** `token` is the Google OIDC ID token from the OAuth redirect. The API verifies it. */
    googleAuth: (token: string, session: SessionContext = {}) =>
      client
        .request<DefaultResponseBody<AuthResult>>("/auth/google", {
          method: "POST",
          body: { token, ...session },
        })
        .then((res) => res.data as AuthResult),

    /** Messengers (Telegram / Bale) the server has a bot for — empty when sign-in with them is off. */
    messengerOptions: () =>
      client
        .request<DefaultResponseBody<{ providers: { provider: string; botUsername: string }[] }>>(
          "/auth/messengers",
        )
        .then((res) => res.data?.providers ?? []),

    /** Step 1 of the bot-based sign-in: the link to open in the messenger and the token to poll with. */
    messengerLoginToken: (provider: string) =>
      client
        .request<DefaultResponseBody<{ token: string; url: string; expiresAt: string }>>(
          `/auth/${provider}/login-token`,
          { method: "POST" },
        )
        .then((res) => res.data as { token: string; url: string; expiresAt: string }),

    /** Step 2: `pending` until the user pressed Start in the bot, then `confirmed` with the session — once. */
    messengerLoginPoll: (provider: string, token: string, session: SessionContext = {}) =>
      client
        .request<
          DefaultResponseBody<{ status: "pending" | "confirmed" | "expired"; auth?: AuthResult }>
        >(`/auth/${provider}/login-token/poll`, {
          method: "POST",
          body: { token, ...session },
        })
        .then(
          (res) => res.data as { status: "pending" | "confirmed" | "expired"; auth?: AuthResult },
        ),

    /** Existing account → `{ isDone: true }`. A new number is sent a register code and `{ isDone: false }`. */
    check: (phone: string) =>
      client
        .request<DefaultResponseBody<{ isDone: boolean }>>("/auth/check", {
          method: "POST",
          body: { phone },
        })
        .then((res) => res.data as { isDone: boolean }),

    /** Login SMS for an account that already exists. */
    loginOtp: (phone: string) =>
      client
        .request<DefaultResponseBody<OtpSendResult>>("/auth/login-otp", {
          method: "POST",
          body: { phone },
        })
        .then((res) => res.data as OtpSendResult),

    /** Forgot-password SMS for an account that already exists. */
    forgetOtp: (phone: string) =>
      client
        .request<DefaultResponseBody<OtpSendResult>>("/auth/forget-otp", {
          method: "POST",
          body: { phone },
        })
        .then((res) => res.data as OtpSendResult),

    /** Another register SMS. Resend on the new-number step — a login SMS would be rejected. */
    registerOtp: (phone: string) =>
      client
        .request<DefaultResponseBody<OtpSendResult>>("/auth/register-otp", {
          method: "POST",
          body: { phone },
        })
        .then((res) => res.data as OtpSendResult),

    /** Confirms the register code without signing in. The same code is sent again with `register`. */
    registerVerify: (phone: string, code: string) =>
      client.request<DefaultResponseBody<null>>("/auth/register-verify", {
        method: "POST",
        body: { phone, code },
      }),

    /** Confirms the reset code without changing the password yet. */
    forgetVerify: (phone: string, code: string) =>
      client.request<DefaultResponseBody<null>>("/auth/forget-verify", {
        method: "POST",
        body: { phone, code },
      }),

    /** Stores the new password. Does not sign in. */
    resetPass: (phone: string, code: string, password: string) =>
      client.request<DefaultResponseBody<null>>("/auth/reset-pass", {
        method: "POST",
        body: { phone, code, password },
      }),

    refreshToken: (token: string) =>
      client
        .request<DefaultResponseBody<AuthResult>>("/auth/refresh", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => res.data as AuthResult),
  };
}
